// standards: allow file-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
import type { DocumentResult, FolderAnalysisProgress, OcrLine } from '../shared/desktop-api';

/**
 * ONE PROGRESS STATE FOR A WORKING SCREEN.
 *
 * Owner report 2026-09-27: people closed the app because the working screens
 * looked frozen or contradicted themselves. The file name showed twice and named
 * two different files, the step list went 1, 2, 3, 3, 4, 2, 3, 4, counters sat
 * at 0 and jumped at the end, and minutes passed with nothing moving.
 *
 * Every one of those came from each element reading its own source: the header
 * read the newest activity label, the page image rotated through the last eight
 * previews on a timer, the counter read whichever wave sorted first, and the
 * step list was hard-wired or ratcheted inside a component that outlived the
 * run. This reducer is the single source. Every element on the screen is drawn
 * from the view it returns, at the same moment.
 *
 * Rules it enforces:
 *   - the stage never goes backwards within a run (parallel lanes and late
 *     workers are shown as activity, not a stage regression);
 *   - ONE count per stage, in one unit (files, pages, pages compared, documents;
 *     during setup, what the current setup step works through), that never goes down; the bar is that count's fraction
 *     and never goes down either; a lane that retires keeps its contribution;
 *   - one current document, matching the work: the page most recently read, the
 *     document being read, or the sample a setup step is looking at;
 *   - the live line names what is happening now without a number of its own
 *     that could disagree with the count (parallel readers finish out of order).
 */

export type WorkingStageId =
  'preparing' | 'recognizing' | 'organizing' | 'understanding' | 'finalizing';
export const workingStages: readonly WorkingStageId[] = [
  'preparing',
  'recognizing',
  'organizing',
  'understanding',
  'finalizing',
];

type EngineStage = FolderAnalysisProgress['stage'] | string;
export type WorkingEvent = Pick<FolderAnalysisProgress, 'stage' | 'completed' | 'total'> &
  Partial<
    Pick<
      FolderAnalysisProgress,
      | 'pageCount'
      | 'documentCount'
      | 'barcodeCount'
      | 'fieldCount'
      | 'lineItemCount'
      | 'documentTypes'
      | 'elapsedMilliseconds'
      | 'stageElapsedMilliseconds'
      | 'activity'
      | 'heartbeat'
      | 'phase'
      | 'phaseElapsedMilliseconds'
      | 'quietMilliseconds'
    >
  > & {
    stage: EngineStage;
    /** A finished document, as the engine hands it over (its type and values are all that is read here). */
    completedDocument?:
      | (Pick<DocumentResult, 'documentType'> & Partial<Pick<DocumentResult, 'id' | 'fields'>>)
      | null;
  };

/** Rank of an engine stage; "complete" ranks past the last stage, unknown stages do not move anything. */
export function workingStageRank(stage: EngineStage | undefined | null) {
  if (stage === 'complete') return workingStages.length;
  // "reading" is the benchmark-only name for the end of recognition.
  if (stage === 'reading') return 1;
  return workingStages.indexOf(stage as WorkingStageId);
}

/**
 * The furthest stage each wave of a job has reported, folded from raw events as they arrive.
 * The newest event's stage is not the job's: a page worker finishing after its wave moved on,
 * or the next wave reading pages beside it, reports "recognizing" while the job is still
 * understanding. Taken at face value, that flipped a new job's first pass from its working
 * screen to document review, and the next report flipped it back (October 5, 2026). A wave's
 * stage only rises; another wave behind it is activity. A new attempt of a wave starts over
 * (`forgetWave`).
 */
export function foldWaveStage(
  waves: ReadonlyMap<string, number>,
  wave: string,
  stage: EngineStage | undefined | null,
): ReadonlyMap<string, number> {
  const rank = workingStageRank(stage);
  if (rank < 0 || (waves.get(wave) ?? -1) >= rank) return waves;
  return new Map(waves).set(wave, rank);
}
export function forgetWave(
  waves: ReadonlyMap<string, number>,
  wave: string,
): ReadonlyMap<string, number> {
  if (!waves.has(wave)) return waves;
  const next = new Map(waves);
  next.delete(wave);
  return next;
}
/** True while some wave of the job has reached understanding and not yet moved past it. */
export const wavesUnderstanding = (waves: ReadonlyMap<string, number>) =>
  [...waves.values()].includes(workingStageRank('understanding'));

/**
 * Setup reasoning happens inside "understanding" before any document is
 * finished. Its four steps move the bar the whole way (instead of a document
 * counter frozen at 0 for minutes); the hero names the step being done and counts
 * what that step works through, never the steps themselves.
 */
const setupSteps = ['type-list', 'type-assignment', 'fields', 'values'] as const;
const setupStepNames: Record<string, string> = {
  'type-list': 'Finding document types',
  'type-assignment': 'Matching samples to types',
  'named-types': 'Matching samples to types',
  fields: 'Choosing fields',
  values: 'Reading sample values',
  readers: 'Starting the readers',
};
/** What a setup step's own count counts, in words a person would use. */
const setupStepUnit = (
  step: (typeof setupSteps)[number],
  stepTotal: number,
  documentTotal: number,
) =>
  step === 'type-list'
    ? 'documents compared'
    : step === 'type-assignment'
      ? 'documents matched to a type'
      : step === 'fields'
        ? stepTotal === 1
          ? 'document type with fields chosen'
          : 'document types with fields chosen'
        : documentTotal > 0 && stepTotal > documentTotal
          ? 'documents and tables read'
          : 'documents read';

const setupStepIndex = (id: string) => {
  const step = id.slice('bootstrap:'.length);
  if (step === 'named-types') return 2; // types named: matching is done, fields are next
  if (step === 'readers') return 0;
  const index = setupSteps.indexOf(step as (typeof setupSteps)[number]);
  return index;
};

type Lane = {
  rank: number;
  /** Highest completed count seen per stage rank. */
  completed: number[];
  /** Highest total seen per stage rank. */
  total: number[];
  pageCount: number;
  documentCount: number;
  barcodeCount: number;
  fieldCount: number;
  lineItemCount: number;
  /** Per setup-step (bootstrap activity id) highest count, so a step count cannot fall either. */
  step: Record<string, { completed: number; total: number }>;
  /** Furthest setup step reached (index into setupSteps), -1 before setup reasoning. */
  setupStep: number;
  inFlightDocuments: Set<string>;
  inFlightPages: Set<string>;
  last?: WorkingEvent;
  /** The newest real (non-heartbeat) activity. */
  activity?: NonNullable<WorkingEvent['activity']>;
  /** Time since the engine last reported real work, as of `at` (a heartbeat says; a real event resets it). */
  quiet?: { ms: number; at: number };
};

export type WorkingDocument = { previewPath: string; label: string; pageNumber?: number };

/**
 * Something the reader found on a finished page, worth one line on the Read step:
 * a decoded barcode, a page it straightened, a blank sheet it set aside. Every
 * one is the engine's own evidence for that page; nothing here is a guess.
 */
export type ReadingFind = {
  id: string;
  kind: 'barcode' | 'straightened' | 'blank' | 'table' | 'spot';
  /** The find itself (a decoded value, an angle), its kind in words, and the live line that announces it. */
  text: string;
  detail: string;
  line: string;
};

/** What the Read, Split and Understand steps say beyond their one count, folded from page evidence and finished documents. */
export type ReadingFacts = {
  /** Finished pages whose evidence was folded in (each page once). */
  pages: number;
  /** Words on those pages: the engine's own exact count per page, while every page said. */
  words: number;
  wordsKnown: boolean;
  lines: number;
  straightened: number;
  blank: number;
  /** Tables and reference numbers spotted on the pages read (see spotPage below). */
  tables: number;
  references: number;
  /** The last few pages read, newest first: what each looks like and the number that leads it. */
  recentPages: {
    id: string;
    title: string | null;
    lead: { label: string; value: string } | null;
  }[];
  /** Newest first, a few at most. */
  finds: ReadingFind[];
  /** What the most recently finished page turned up, or null when it found nothing to call out. */
  latest: ReadingFind | null;
  /**
   * True when this screen saw the run from its first page. A screen that came back
   * mid-run (a reload, a restart) has only the pages read since, so it never shows
   * a running word total that would undercount.
   */
  fromStart: boolean;
  /** The last finished document: its type and a value or two it read. */
  lastDocument: { type: string; values: { name: string; value: string }[] } | null;
};

export type WorkingState = {
  runKey: string;
  rank: number;
  lanes: Record<string, Lane>;
  /** Events already folded in, so re-rendering with the same objects is idempotent. */
  seen: WeakSet<object>;
  current: WorkingDocument | null;
  /** Names of recent previews, so the caption can name the image actually on screen. */
  previews: WorkingDocument[];
  /** Pages finished reading, newest first (for the thumbnail strip). */
  finishedPages: WorkingDocument[];
  /** Highest bar fraction shown per stage: the bar never slides back. */
  barByStage: number[];
  /** Highest count shown per stage (for its total and unit): the hero never goes down. */
  heroByStage: { completed: number; total: number; unit?: string }[];
  /** When the step the operator is watching began (stage, or named setup step). */
  stepKey: string;
  stepStartedAt: number;
  /** When the furthest stage was reached, for the time-left estimate. */
  stageStartedAt: number;
  lastEventAt: number;
  startedAt: number;
  types: string[];
  /** The engine's newest named phase from a heartbeat, and when it was reported. */
  phase: { text: string; elapsed: number; at: number } | null;
  reading: ReadingFacts;
  /**
   * The run clock as last shown: the engine's elapsed time, carried forward and
   * never set back. A late or re-sent report (a heartbeat, main's retained copy
   * after a remount) is older than the moment it is folded in; taking it at face
   * value held the clock still for seconds.
   */
  runClock: { elapsed: number; at: number } | null;
  /** Pages (lane:activity) already counted into `reading`. */
  readPages: ReadonlySet<string>;
};

const emptyReading = (): ReadingFacts => ({
  pages: 0,
  words: 0,
  wordsKnown: true,
  lines: 0,
  straightened: 0,
  blank: 0,
  tables: 0,
  references: 0,
  recentPages: [],
  finds: [],
  latest: null,
  fromStart: true,
  lastDocument: null,
});

/** "Code39" reads as "Code 39", "QRCode" as "QR Code". */
export function symbologyName(value: string | null | undefined) {
  const raw = (value ?? '').trim();
  if (!raw) return 'Barcode';
  return raw
    .replace(/_/g, ' ')
    .replace(/([a-z])([A-Z0-9])/g, '$1 $2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
    .replace(/([A-Za-z])(\d)/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim();
}

const degrees = (value: number) =>
  `${Math.abs(value).toLocaleString('en-US', { maximumFractionDigits: 1, minimumFractionDigits: 1 })}°`;

const empty = (
  runKey: string,
  now: number,
  seen: WeakSet<object> = new WeakSet(),
): WorkingState => ({
  runKey,
  rank: -1,
  lanes: {},
  seen,
  current: null,
  previews: [],
  finishedPages: [],
  barByStage: [],
  heroByStage: [],
  stepKey: '',
  stepStartedAt: now,
  stageStartedAt: now,
  lastEventAt: now,
  startedAt: now,
  types: [],
  phase: null,
  reading: emptyReading(),
  readPages: new Set(),
  runClock: null,
});

const newLane = (): Lane => ({
  rank: -1,
  completed: [],
  total: [],
  pageCount: 0,
  documentCount: 0,
  barcodeCount: 0,
  fieldCount: 0,
  lineItemCount: 0,
  step: {},
  setupStep: -1,
  inFlightDocuments: new Set(),
  inFlightPages: new Set(),
});

const count = (value: unknown) =>
  typeof value === 'number' && Number.isFinite(value) && value > 0 ? Math.floor(value) : 0;

/** A lane is finished once it reports "complete". */
const laneDone = (lane: Lane) => lane.rank >= workingStages.length;

/** Whole-page activities; a page's :ocr and :barcode sub-steps are not pages of their own. */
const pageActivity = (id: string) => /^page:\d+$/.test(id);

/**
 * Fold the current per-lane events into the state. Lanes are independent units
 * of engine work that may overlap (waves of one scan); the caller hands every
 * lane it currently knows. A lane that is no longer handed over keeps what it
 * contributed. A new run starts only when the caller's run key changes.
 *
 * A lane that appears after every earlier lane finished is NOT a new run: a
 * folder import's waves run one at a time (b80c4ca2), so each later wave starts
 * after the one before it finished, and restarting here put the steps back at
 * Prepare for every wave (owner, 6.2.0, 2026-09-29: "Prepare, Read, Split,
 * Understand, then back to Prepare, Read, Split, then back to Read"). The caller
 * names the import in its run key, so a genuinely new import starts fresh.
 */
export function advanceWorkingProgress(
  previous: WorkingState | null,
  runKey: string,
  lanes: ReadonlyArray<{ lane: string; event: WorkingEvent | null | undefined }>,
  now = Date.now(),
): WorkingState {
  let state = previous && previous.runKey === runKey ? previous : empty(runKey, now);
  const incoming = lanes.filter((item): item is { lane: string; event: WorkingEvent } =>
    Boolean(item.event),
  );
  let changed = false;
  // Entries fold in order; a caller may hand a lane's whole batch of events (so
  // no finished page is skipped between screen refreshes) followed by its latest.
  const next: WorkingState = { ...state, lanes: { ...state.lanes } };
  for (const { lane: id, event } of incoming) {
    if (next.seen.has(event)) continue;
    next.seen.add(event);
    changed = true;
    const prior = next.lanes[id];
    const lane: Lane = prior
      ? {
          ...prior,
          completed: [...prior.completed],
          total: [...prior.total],
          step: { ...prior.step },
          inFlightDocuments: new Set(prior.inFlightDocuments),
          inFlightPages: new Set(prior.inFlightPages),
        }
      : newLane();
    const rank = workingStageRank(event.stage);
    if (rank >= 0) {
      lane.rank = Math.max(lane.rank, rank);
      lane.completed[rank] = Math.max(lane.completed[rank] ?? 0, count(event.completed));
      lane.total[rank] = Math.max(lane.total[rank] ?? 0, count(event.total));
    }
    lane.pageCount = Math.max(lane.pageCount, count(event.pageCount));
    lane.documentCount = Math.max(lane.documentCount, count(event.documentCount));
    lane.barcodeCount = Math.max(lane.barcodeCount, count(event.barcodeCount));
    lane.fieldCount = Math.max(lane.fieldCount, count(event.fieldCount));
    lane.lineItemCount = Math.max(lane.lineItemCount, count(event.lineItemCount));
    if (!event.heartbeat) lane.quiet = { ms: 0, at: now };
    else if (typeof event.quietMilliseconds === 'number')
      lane.quiet = { ms: count(event.quietMilliseconds), at: now };
    const activity = event.activity;
    if (activity && !event.heartbeat) {
      lane.activity = activity;
      const started = activity.status === 'started' || activity.status === 'queued';
      if (activity.kind === 'document') {
        if (started) lane.inFlightDocuments.add(activity.id);
        else lane.inFlightDocuments.delete(activity.id);
      }
      if (pageActivity(activity.id)) {
        if (started) lane.inFlightPages.add(activity.id);
        else lane.inFlightPages.delete(activity.id);
      }
      // "bootstrap:readers" is sent on every run, setup or not; only real setup steps count.
      if (activity.id.startsWith('bootstrap:') && activity.id !== 'bootstrap:readers')
        lane.setupStep = Math.max(lane.setupStep, setupStepIndex(activity.id));
      if ((activity.stepTotal ?? 0) > 0) {
        const seen = lane.step[activity.id];
        lane.step[activity.id] = {
          completed: Math.max(seen?.completed ?? 0, count(activity.stepCompleted)),
          total: Math.max(seen?.total ?? 0, count(activity.stepTotal)),
        };
      }
      if (activity.previewPath) {
        // Setup steps name the step, not the file; the file's own name was
        // learned when its page was read.
        const setupStep = activity.id.startsWith('bootstrap:');
        const known = next.previews.find((item) => item.previewPath === activity.previewPath);
        const document: WorkingDocument = {
          previewPath: activity.previewPath,
          label: setupStep ? (known?.label ?? '') : activity.label?.trim() || known?.label || '',
          // The caption sits beside the file's name, so it is the page within that
          // file, never the page's place in the batch (0 = unknown, no caption).
          pageNumber: count(activity.sourcePageNumber) || known?.pageNumber,
        };
        if (!setupStep || !known)
          next.previews = [
            document,
            ...next.previews.filter((item) => item.previewPath !== document.previewPath),
          ].slice(0, 48);
        const finishedPage = pageActivity(activity.id) && activity.status === 'completed';
        if (finishedPage)
          next.finishedPages = [
            document,
            ...next.finishedPages.filter((item) => item.previewPath !== document.previewPath),
          ].slice(0, 12);
        // The page on screen is the work: the page most recently READ (a started
        // page only until one has finished), the document being read, the sample
        // a setup step is looking at.
        const showsWork =
          finishedPage ||
          (pageActivity(activity.id) && started && next.finishedPages.length === 0) ||
          (activity.kind === 'document' && started) ||
          setupStep ||
          activity.kind === 'source';
        if (showsWork) next.current = setupStep && known ? known : document;
      }
    }
    // The screen's first word decides whether it saw the run begin: an opening
    // "0 of N" (or anything before reading), not a count already under way.
    if (
      !prior &&
      Object.keys(next.lanes).length === 0 &&
      next.reading.pages === 0 &&
      (rank > 1 || (rank === 1 && count(event.completed) > 0))
    )
      next.reading = { ...next.reading, fromStart: false };
    const pageKey = activity ? `${id}:${activity.id}` : '';
    if (
      activity &&
      !event.heartbeat &&
      pageActivity(activity.id) &&
      activity.status === 'completed' &&
      activity.evidence &&
      !next.readPages.has(pageKey)
    ) {
      const evidence = activity.evidence;
      next.readPages = new Set([...next.readPages, pageKey]);
      const reading = { ...next.reading };
      const found: ReadingFind[] = [];
      reading.pages += 1;
      reading.lines += count(evidence.lineCount);
      if (typeof evidence.wordCount === 'number' && evidence.wordCount >= 0)
        reading.words += count(evidence.wordCount);
      else reading.wordsKnown = false;
      // What the page shows a person: its tables and the numbers they look for.
      // These only feed the live line and the running counts; never a field value.
      const spotted = spotPage(evidence.lines ?? []);
      reading.tables += spotted.tables.length;
      reading.references += spotted.spots.filter((spot) => spot.kind === 'reference').length;
      const lead =
        spotted.spots.find((spot) => spot.kind === 'total') ??
        spotted.spots.find((spot) => spot.kind === 'reference');
      const pageTitle = spotted.spots.find((spot) => spot.kind === 'title')?.value ?? null;
      if (pageTitle || lead)
        reading.recentPages = [
          {
            id: pageKey,
            title: pageTitle,
            lead: lead ? { label: lead.label, value: lead.value } : null,
          },
          ...reading.recentPages,
        ].slice(0, 4);
      for (const [index, table] of spotted.tables.entries())
        found.push({
          id: `${pageKey}:table:${index}`,
          kind: 'table',
          text: plural(table.rows.length, 'row', 'rows'),
          detail: 'Table',
          line: `Found a table · ${plural(table.rows.length, 'row', 'rows')}`,
        });
      if (lead)
        found.push({
          id: `${pageKey}:spot`,
          kind: 'spot',
          text: lead.value,
          detail: lead.label,
          line: `Spotted ${lead.label.toLocaleLowerCase()} ${lead.value}`,
        });
      const skew = Number(evidence.deskewDegrees ?? 0);
      if (Number.isFinite(skew) && Math.abs(skew) >= 0.05) {
        reading.straightened += 1;
        found.push({
          id: `${pageKey}:straightened`,
          kind: 'straightened',
          text: degrees(skew),
          detail: 'Page straightened',
          line: `Straightened a page by ${degrees(skew)}`,
        });
      }
      if (evidence.blank) {
        reading.blank += 1;
        found.push({
          id: `${pageKey}:blank`,
          kind: 'blank',
          text: 'Set aside',
          detail: 'Blank page',
          line: 'Set a blank page aside',
        });
      }
      for (const [index, code] of (evidence.barcodes ?? []).entries()) {
        const value = code.value?.trim();
        if (value)
          found.push({
            id: `${pageKey}:barcode:${index}`,
            kind: 'barcode',
            text: value,
            detail: symbologyName(code.symbology),
            line: `Found barcode ${symbologyName(code.symbology)} · ${value}`,
          });
      }
      // The most specific find (a decoded value) leads the live line. The list keeps
      // what is worth a second look: each decoded value once (a two-page document
      // carries its barcode twice) and blank pages; a straightened page is counted
      // and shown turning on the page, not listed.
      found.reverse();
      // Once across pages and within one page (a page can carry the same code twice).
      const codesListed = new Set(
        reading.finds.filter((seen) => seen.kind === 'barcode').map((seen) => seen.text),
      );
      const listed = found.filter((find) => {
        if (find.kind === 'straightened' || find.kind === 'table' || find.kind === 'spot')
          return false;
        if (find.kind !== 'barcode') return true;
        if (codesListed.has(find.text)) return false;
        codesListed.add(find.text);
        return true;
      });
      reading.finds = [...listed, ...reading.finds].slice(0, 6);
      reading.latest = found[0] ?? null;
      next.reading = reading;
    }
    const finishedDocument = event.completedDocument;
    if (finishedDocument && !event.heartbeat && finishedDocument.documentType?.trim()) {
      const values = (finishedDocument.fields ?? [])
        .filter(
          (field) =>
            field.name !== 'Document type' && field.name !== 'Line items' && field.value?.trim(),
        )
        .slice(0, 2)
        .map((field) => ({ name: field.name, value: field.value!.trim() }));
      next.reading = {
        ...next.reading,
        lastDocument: { type: finishedDocument.documentType.trim(), values },
      };
    }
    for (const name of event.documentTypes ?? []) {
      const trimmed = name?.trim();
      if (trimmed && !next.types.includes(trimmed)) next.types = [...next.types, trimmed];
    }
    if (event.heartbeat && event.phase?.trim())
      next.phase = {
        text: event.phase.trim(),
        elapsed: count(event.phaseElapsedMilliseconds),
        at: now,
      };
    else if (!event.heartbeat) next.phase = null;
    lane.last = event;
    next.lanes[id] = lane;
    if (lane.rank > next.rank) {
      next.rank = lane.rank;
      next.stageStartedAt = now;
    }
  }
  if (!changed) return state;
  const engineElapsed = Math.max(
    0,
    ...Object.values(next.lanes).map((lane) => count(lane.last?.elapsedMilliseconds)),
  );
  if (engineElapsed > 0)
    next.runClock = {
      elapsed: Math.max(
        engineElapsed,
        next.runClock ? next.runClock.elapsed + Math.max(0, now - next.runClock.at) : 0,
      ),
      at: now,
    };
  next.lastEventAt = now;
  const view = describe(next);
  next.barByStage = [...next.barByStage];
  next.barByStage[view.stageIndex] = view.bar;
  next.heroByStage = [...next.heroByStage];
  next.heroByStage[view.stageIndex] = {
    completed: view.completed,
    total: view.total,
    unit: view.unit,
  };
  const stepKey = `${view.stageId}|${view.stepId}`;
  if (stepKey !== next.stepKey) {
    next.stepKey = stepKey;
    next.stepStartedAt = now;
  }
  return next;
}

export type WorkingView = {
  /** Furthest committed stage, clamped to the last stage for the step list. */
  stageId: WorkingStageId;
  stageIndex: number;
  /** The stage's one count, never decreasing. */
  completed: number;
  total: number;
  /** True when total is known, so a determinate bar is honest. */
  counted: boolean;
  /** What the count counts, one unit per stage, e.g. "pages read". */
  unit: string;
  /** 0..1, never decreasing within a stage; agrees with completed/total. */
  bar: number;
  /** What is happening now, in words, with no count that could disagree with the hero. */
  subStep: string;
  /** Identity of the current step. */
  stepId: string;
  pageCount: number;
  documentCount: number;
  barcodeCount: number;
  fieldCount: number;
  lineItemCount: number;
  documentsInFlight: number;
  pagesInFlight: number;
  /** Lanes still working at an earlier stage, shown as activity rather than moving the stage back. */
  lanesBehind: number;
  types: readonly string[];
  /** The document type whose fields are being chosen right now. */
  activeType: string | null;
  /**
   * While a local reasoner chooses that type's fields: its answers so far, how many a type has
   * taken in this setup (0 until one is done) and the measured time per answer; with the time
   * since its last word. Null on every other step and for engines that do not count answers.
   */
  question: {
    answered: number;
    expected: number;
    averageMilliseconds: number;
    quiet: { ms: number; at: number } | null;
    typesDone: number;
    typesTotal: number;
  } | null;
  current: WorkingDocument | null;
  finished: boolean;
  reading: ReadingFacts;
  /** The newest real activity's id and the engine's own label for it (a straightening pass, a setup step). */
  activityId: string | null;
  activityLabel: string | null;
  /** The long wait a heartbeat names right now (ProgressStream phase), or null. */
  phaseText: string | null;
};

const stageUnits: Record<WorkingStageId, string> = {
  preparing: 'files prepared',
  recognizing: 'pages read',
  organizing: 'pages compared',
  understanding: 'documents read',
  finalizing: 'saved',
};

const sum = (values: number[]) => values.reduce((total, value) => total + value, 0);
const plural = (value: number, one: string, many: string) =>
  `${value.toLocaleString()} ${value === 1 ? one : many}`;

/**
 * The step every list of the five steps shows for the import on screen (owner, 2026-10-03:
 * "the user should never see the wizard go backwards"): the step the import's run has reached,
 * so a later wave starting at Prepare is activity, not a step back; and every step done for
 * the import a job's setup started, since its wizard ended there. The capture strip used to
 * read each wave's own stage and went back to Prepare at every wave.
 */
export function importStepShown(
  state: WorkingState | null,
  afterSetup: boolean,
): WorkingStageId | 'complete' | null {
  if (afterSetup) return 'complete';
  return state && state.rank >= 0 ? describe(state).stageId : null;
}

/** The one view every element on a working screen is drawn from. */
// standards: allow function-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
export function describe(state: WorkingState): WorkingView {
  const lanes = Object.values(state.lanes);
  const rank = Math.max(0, state.rank);
  const stageIndex = Math.min(rank, workingStages.length - 1);
  const stageId = workingStages[stageIndex];
  const finished = lanes.length > 0 && lanes.every(laneDone);
  const atStage = lanes.filter((lane) => lane.rank >= rank);
  let completed = sum(atStage.map((lane) => lane.completed[rank] ?? 0));
  let total = sum(atStage.map((lane) => lane.total[rank] ?? 0));
  if (rank >= workingStages.length) {
    completed = 1;
    total = 1;
  }
  let unit = stageUnits[stageId];
  let subStep = '';
  let stepId = '';
  let fraction = total > 0 ? Math.min(1, completed / total) : 0;
  const documentsInFlight = sum(lanes.map((lane) => lane.inFlightDocuments.size));
  const pagesInFlight = sum(atStage.map((lane) => lane.inFlightPages.size));
  const lanesBehind = lanes.filter((lane) => !laneDone(lane) && lane.rank < rank).length;
  const leader = [...atStage].sort((left, right) => right.rank - left.rank)[0];
  const activity = leader?.activity;
  const setup = stageId === 'understanding' && leader && leader.setupStep >= 0;
  if (setup) {
    // No "N of 4" here (owner, 2026-09-29: a count of four steps beside the five-stage
    // stepper reads as the stepper's own number). The hero names the step being done
    // ("Choosing useful fields for Credit Memo"); the one count is of real things that
    // step works through (documents compared, document types with fields, documents
    // read), and the bar moves across the whole setup stretch without sliding back.
    const step = Math.min(leader.setupStep, setupSteps.length - 1);
    // The bar is the furthest step's progress, so it never slides back.
    const furthest = leader.step[`bootstrap:${setupSteps[step]}`];
    const documentsDone = total > 0 ? completed / total : 0;
    const furthestDone = furthest && furthest.total > 0 ? furthest.completed / furthest.total : 0;
    const within =
      step === setupSteps.length - 1 ? Math.max(furthestDone, documentsDone) : furthestDone;
    // The count is of the furthest step, the one the screen names (stepId). A report of an
    // earlier step after it (late, from an older snapshot, or a later operation going over it
    // again) is activity in the live line: counting it turned "Trying the fields, 3 of 18
    // documents" into "1 of 2 documents", a count of document types under the documents
    // label (first-pages-run, October 5, 2026).
    const nowStep = setupSteps[step];
    const own = furthest;
    const ownDone = own && own.total > 0 ? own.completed / own.total : 0;
    const lastStep = nowStep === 'values';
    if (own && own.total > 0 && !(lastStep && documentsDone > ownDone)) {
      completed = Math.min(own.completed, own.total);
      unit = setupStepUnit(nowStep, own.total, total);
      total = own.total;
    } else if (lastStep && total > 0) {
      // The documents that finish at the end of setup are the last step's own count.
      unit = 'documents read';
    } else {
      completed = 0;
      total = 0;
      unit = '';
    }
    fraction = Math.min(1, (step + Math.min(1, within)) / setupSteps.length);
    stepId = setupSteps[step];
    const name =
      setupStepNames[activity?.id.slice('bootstrap:'.length) ?? ''] ??
      setupStepNames[setupSteps[step]];
    subStep = activity?.id.startsWith('bootstrap:')
      ? activity.label || name
      : documentsInFlight > 0
        ? `Pulling values from ${plural(documentsInFlight, 'sample', 'samples')}`
        : name;
  } else if (stageId === 'preparing') {
    subStep = 'Preparing the files';
  } else if (stageId === 'recognizing') {
    // What the reader just did, from the page that just finished: the barcode it
    // decoded, the tilt it took out, the table or number it spotted. No page number:
    // the count above and the caption under the page are the numbers.
    const reading = state.reading;
    subStep =
      completed >= total && total > 0
        ? 'Checking the last pages'
        : reading.latest
          ? reading.latest.line
          : pagesInFlight > 1
            ? `Reading ${pagesInFlight.toLocaleString()} pages at once`
            : pagesInFlight === 1
              ? 'Reading one page'
              : 'Reading pages';
  } else if (stageId === 'organizing') {
    const found = sum(lanes.map((lane) => lane.documentCount));
    subStep =
      found > 0
        ? `${plural(found, 'document', 'documents')} found in these pages`
        : 'Finding where each document starts';
  } else if (stageId === 'understanding') {
    // The document that just finished, by what it is and a value it read.
    const last = state.reading.lastDocument;
    const value = last?.values[0];
    subStep =
      completed >= total && total > 0
        ? 'Checking the last documents'
        : last
          ? `${last.type} read${value ? ` · ${value.name} ${value.value}` : ''}`
          : documentsInFlight > 1
            ? `Pulling values from ${documentsInFlight.toLocaleString()} documents at once`
            : 'Pulling values from the next document';
  } else {
    // One wave saving while another, or a document of its own, is still being read is not
    // "Saving the results": a 45-file setup said that for 12 minutes while the engine was still
    // reading its last documents (2026-10-15). The engine's named wait, below, comes first.
    subStep =
      lanesBehind > 0 || documentsInFlight > 0
        ? 'Finishing the last documents'
        : 'Saving the results';
  }
  if (state.phase) subStep = state.phase.text;
  // No "2 more batches on the way": waves are scheduling, and a wave count means
  // nothing to the person who chose one folder (owner, 2026-09-27).
  const bar = Math.max(fraction, state.barByStage[stageIndex] ?? 0);
  const shown = state.heroByStage[stageIndex];
  if (shown && shown.total === total && (shown.unit ?? unit) === unit)
    completed = Math.max(completed, shown.completed);
  return {
    stageId,
    stageIndex,
    completed,
    total,
    counted: total > 0,
    unit,
    bar,
    subStep,
    stepId,
    pageCount: sum(lanes.map((lane) => lane.pageCount)),
    documentCount: sum(lanes.map((lane) => lane.documentCount)),
    barcodeCount: sum(lanes.map((lane) => lane.barcodeCount)),
    fieldCount: sum(lanes.map((lane) => lane.fieldCount)),
    lineItemCount: sum(lanes.map((lane) => lane.lineItemCount)),
    documentsInFlight,
    pagesInFlight,
    lanesBehind,
    types: state.types,
    activeType: setup ? activeSetupType(activity) : null,
    question:
      setup && activeSetupType(activity) && typeof activity?.questionsAnswered === 'number'
        ? {
            answered: count(activity.questionsAnswered),
            expected: count(activity.questionsExpected),
            averageMilliseconds: count(activity.answerMilliseconds),
            quiet: leader?.quiet ?? null,
            typesDone: count(activity.stepCompleted),
            typesTotal: count(activity.stepTotal),
          }
        : null,
    current: state.current,
    finished,
    reading: state.reading,
    activityId: activity?.id ?? null,
    activityLabel: activity?.label?.trim() || null,
    phaseText: state.phase?.text ?? null,
  };
}

/**
 * The type a fields step is working on: named by the event itself, or (for
 * engines before it said so) by the engine's own "Choosing useful fields for X".
 * A type whose fields are ready, or any other step, has no active type.
 */
function activeSetupType(activity: Lane['activity']) {
  if (activity?.id !== 'bootstrap:fields' || activity.status !== 'started' || activity.tags?.length)
    return null;
  const named =
    activity.discoveredDocumentTypes?.length === 1 ? activity.discoveredDocumentTypes[0] : null;
  return (
    (named ?? /^Choosing useful fields for (.+)$/.exec(activity.label ?? '')?.[1] ?? '').trim() ||
    null
  );
}

/** The caption for the image that is actually on screen: named by the path that loaded, never by the newest event. */
export function workingPreviewIdentity(
  state: WorkingState | null,
  loadedPath: string | null | undefined,
) {
  if (!state || !loadedPath) return null;
  return state.previews.find((item) => item.previewPath === loadedPath) ?? null;
}

/** m:ss (or h:mm:ss); moves every second while work runs. */
export function stepClock(milliseconds: number) {
  const seconds = Math.max(0, Math.floor(milliseconds / 1000));
  const minutes = Math.floor(seconds / 60);
  if (minutes >= 60)
    return `${Math.floor(minutes / 60)}:${String(minutes % 60).padStart(2, '0')}:${String(seconds % 60).padStart(2, '0')}`;
  return `${minutes}:${String(seconds % 60).padStart(2, '0')}`;
}

/**
 * The live line while a type's fields are chosen, one answer at a time: which question the
 * reasoner is on, of about how many a type has taken in this setup, and the time on it, moving
 * every second. A processor takes up to a few minutes per answer; this line sat empty while the
 * screen showed only the type's name for the whole type (Paige V2's measurement, 2026-10-02).
 * After about two answers' worth of silence (at least a minute) it says plainly that it is still
 * working, instead of letting a long answer look like a hang. Never a model name. Past the
 * estimate it drops "of about n" rather than chasing the count ("10 of about 10" read as
 * nothing, owner, October 3, 2026), and never says "4 of about 3".
 */
export function questionLine(question: NonNullable<WorkingView['question']>, now: number) {
  const number = question.answered + 1;
  const about =
    question.expected > 0 && number <= question.expected
      ? ` of about ${question.expected.toLocaleString()}`
      : '';
  const quietFor = question.quiet ? question.quiet.ms + Math.max(0, now - question.quiet.at) : 0;
  const slow =
    question.averageMilliseconds > 0 ? Math.max(60_000, 2 * question.averageMilliseconds) : 90_000;
  return quietFor >= slow
    ? `Still working on question ${number.toLocaleString()}${about} · ${stepClock(quietFor)}`
    : `Question ${number.toLocaleString()}${about} · ${stepClock(quietFor)}`;
}

/**
 * Time left choosing fields, from this setup's own measured time per answer: what is left of the
 * question being answered, the rest of this type's usual questions, and the types still to come.
 * Null until a type has been done (no measured number to stand on). On a processor the documents
 * are read while the next type is designed, so this is nearly all of the setup left.
 */
export function questionRemainingMilliseconds(
  question: NonNullable<WorkingView['question']>,
  now: number,
) {
  const average = question.averageMilliseconds;
  if (question.expected <= 0 || average <= 0) return null;
  const quietFor = question.quiet ? question.quiet.ms + Math.max(0, now - question.quiet.at) : 0;
  const typesAfter = Math.max(0, question.typesTotal - question.typesDone - 1);
  const milliseconds =
    Math.max(0, average - quietFor) +
    Math.max(0, question.expected - question.answered - 1) * average +
    typesAfter * question.expected * average;
  return milliseconds > 0 ? milliseconds : null;
}

export type WorkingReadout = {
  stageId: WorkingStageId;
  headline: string;
  detail: string;
  valueText: string;
  completed?: number;
  total?: number;
  /** 0..1 bar, never sliding back within a stage. */
  bar: number;
  indeterminate: boolean;
  /** The headline names the setup step being done; the count, when there is one, is in the detail. */
  named?: boolean;
  live: { text: string };
  activeType: string | null;
  /** A fixed set of facts from the same state, the same tiles from the first frame to the last. */
  metrics: { label: string; value: string }[];
  /** Processing details: the same state's totals, nothing the main view could disagree with. */
  details: { label: string; value: string }[];
  remainingMilliseconds: number | null;
};

/**
 * Everything the working screen says, from one state at one moment.
 * The count is the hero from the moment its total is known, including a genuine
 * "0 of 23": a blank counter while work runs reads as stuck. The screen shows the
 * work moving while that count is 0 with the bar's shimmer and the live line.
 */
export function workingReadout(
  state: WorkingState,
  now = Date.now(),
  options: { unit?: Partial<Record<WorkingStageId, string>>; elapsedMilliseconds?: number } = {},
): WorkingReadout {
  const view = describe(state);
  const unit =
    options.unit?.[view.stageId] && !view.stepId ? options.unit[view.stageId]! : view.unit;
  // The engine's own stage clock (plus the time since its last word) is truer than
  // when this screen first saw the stage: a screen opened mid-stage would price
  // the rest of it from a few seconds of watching.
  const engineStage = Math.max(
    0,
    ...Object.values(state.lanes)
      .filter((lane) => lane.rank === state.rank)
      .map((lane) => count(lane.last?.stageElapsedMilliseconds)),
  );
  const stageMilliseconds = Math.max(
    now - state.stageStartedAt,
    engineStage > 0 ? engineStage + Math.max(0, now - state.lastEventAt) : 0,
  );
  const remaining =
    view.counted &&
    !view.stepId &&
    view.completed >= 3 &&
    view.completed < view.total &&
    stageMilliseconds > 0
      ? (stageMilliseconds / view.completed) * (view.total - view.completed)
      : null;
  // The engine's own run clock where it has one (a screen opened mid-run did not
  // see the start), moving on by the time since it last spoke.
  const elapsed =
    options.elapsedMilliseconds ??
    Math.max(
      now - state.startedAt,
      state.runClock ? state.runClock.elapsed + Math.max(0, now - state.runClock.at) : 0,
    );
  const metrics = [
    { label: 'Elapsed', value: stepClock(elapsed) },
    { label: 'Left', value: remaining ? `~${stepClock(remaining)}` : 'Estimating…' },
  ];
  const details = [
    { label: 'Pages', value: view.pageCount.toLocaleString() },
    { label: 'Barcodes', value: view.barcodeCount.toLocaleString() },
    { label: 'Documents', value: view.documentCount.toLocaleString() },
    { label: 'Types', value: view.types.length.toLocaleString() },
    { label: 'Values read', value: view.fieldCount.toLocaleString() },
    { label: 'Line items', value: view.lineItemCount.toLocaleString() },
  ];
  if (view.stepId) {
    // Setup inside Understand: the hero names the step being done and the detail carries its
    // one real count ("2 of 5 document types with fields chosen"). Never "N of 4" beside the
    // five-stage stepper (owner, 2026-09-29). The bar is the whole setup stretch, never sliding back.
    const counted = view.counted
      ? `${view.completed.toLocaleString()} of ${view.total.toLocaleString()} ${view.unit}`
      : '';
    const headline = view.subStep || 'Setting up document types';
    const fieldsLeft = view.question ? questionRemainingMilliseconds(view.question, now) : null;
    const setupMetrics = fieldsLeft
      ? [metrics[0], { label: 'Left', value: `~${stepClock(fieldsLeft)}` }]
      : metrics;
    return {
      stageId: view.stageId,
      headline,
      detail: counted,
      valueText: counted ? `${headline}. ${counted}.` : headline,
      bar: view.bar,
      indeterminate: false,
      named: true,
      live: { text: view.question ? questionLine(view.question, now) : '' },
      activeType: view.activeType,
      metrics: setupMetrics,
      details,
      remainingMilliseconds: fieldsLeft,
    };
  }
  if (view.counted) {
    const headline = `${view.completed.toLocaleString()} of ${view.total.toLocaleString()}`;
    return {
      stageId: view.stageId,
      headline,
      detail: unit,
      valueText: `${headline} ${unit}. ${view.subStep}`,
      completed: view.completed,
      total: view.total,
      bar: view.bar,
      indeterminate: false,
      live: { text: view.subStep },
      activeType: view.activeType,
      metrics,
      details,
      remainingMilliseconds: remaining,
    };
  }
  // No total yet: the hero still never holds a sentence; the live line names the step.
  return {
    stageId: view.stageId,
    headline: '–',
    detail: unit,
    valueText: view.subStep,
    bar: 0,
    indeterminate: true,
    live: { text: view.subStep || 'Starting' },
    activeType: view.activeType,
    metrics,
    details,
    remainingMilliseconds: null,
  };
}

/**
 * What a person would notice on a page at a glance, from the reader's own lines:
 * the tables, the blocks of text, the title, the reference numbers, the dates and
 * the total. The Read step draws these on the page and lists them beside it
 * (owner, 2026-09-28: "words read" means nothing to anyone; show what matters
 * about their documents). This is a picture of the reading, never a field value:
 * nothing here is saved, learned from or exported.
 */
export type Box = { x: number; y: number; width: number; height: number };
export type SpotKind = 'title' | 'reference' | 'date' | 'total';
export type Spot = { kind: SpotKind; label: string; value: string; box: Box };
export type TableSpot = { box: Box; rows: Box[] };
export type PageSpots = { blocks: Box[]; tables: TableSpot[]; spots: Spot[] };

type Placed = { text: string; box: Box; mid: number };

const boxOf = (line: OcrLine): Box | null => {
  const points = line.polygon.filter(
    (point) => Number.isFinite(point.x) && Number.isFinite(point.y),
  );
  if (!points.length) return null;
  const x = Math.min(...points.map((p) => p.x)),
    y = Math.min(...points.map((p) => p.y));
  const width = Math.max(...points.map((p) => p.x)) - x,
    height = Math.max(...points.map((p) => p.y)) - y;
  return width > 0 && height > 0 ? { x, y, width, height } : null;
};
export const union = (boxes: readonly Box[]): Box => {
  const x = Math.min(...boxes.map((b) => b.x)),
    y = Math.min(...boxes.map((b) => b.y));
  return {
    x,
    y,
    width: Math.max(...boxes.map((b) => b.x + b.width)) - x,
    height: Math.max(...boxes.map((b) => b.y + b.height)) - y,
  };
};
const median = (values: number[]) =>
  [...values].sort((a, b) => a - b)[Math.floor(values.length / 2)] ?? 0;

const numeric = /^[\s$€£(]*[-+]?[\d.,]*\d[\d.,]*[)%]?\s*$/;
const dateLike =
  /\b(\d{1,2}[/.-]\d{1,2}[/.-]\d{2,4}|\d{4}-\d{2}-\d{2}|(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)[a-z]*\.?\s+\d{1,2},?\s+\d{4})\b/i;
const referenceLike =
  /\b(?=[A-Z0-9-]*\d)(?=[A-Z0-9-]*[A-Z])[A-Z0-9]+(?:-[A-Z0-9]+)+\b|\b[A-Z]{2,}\d{4,}\b/;
const money = /[-+]?\$?\s?\d{1,3}(?:,\d{3})*(?:\.\d{2})\b|[-+]?\$?\s?\d+\.\d{2}\b/g;
const labelText = (text: string) => text.replace(/[:#.\s]+$/, '').trim();
const isLabel = (text: string) => text.length <= 26 && !/\d/.test(text) && /[A-Za-z]/.test(text);
/** A label names the value's role; a person's or company's name beside it does not. */
const roleWord =
  /(^|[^a-z])(no|nr|number|num|#|id|ref|reference|order|invoice|inv|p\.?\s?o|account|acct|customer|claim|policy|report|req|date|dated|due|period|issued|ship|shipped|delivery|need|deliver|statement|quote|job|case|file|ticket|bol|lading)([^a-z]|$)/i;
const titleWords =
  /^(invoice|purchase\s*order|expense\s*report|statement|receipt|credit memo|debit memo|bill of lading|quote|quotation|estimate|packing (slip|list)|remittance|claim|work order|sales order|delivery (note|receipt)|proof of delivery|agreement|contract|application|notice)$/i;

/** Lines whose middles sit on the same level, left to right. */
function rowsOf(lines: Placed[], height: number): Placed[][] {
  const rows: Placed[][] = [];
  for (const line of [...lines].sort((a, b) => a.mid - b.mid)) {
    const row = rows.find((candidate) => Math.abs(candidate[0].mid - line.mid) <= height * 0.5);
    if (row) row.push(line);
    else rows.push([line]);
  }
  for (const row of rows) row.sort((a, b) => a.box.x - b.box.x);
  return rows.sort((a, b) => a[0].mid - b[0].mid);
}

// standards: allow function-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
export function spotPage(ocrLines: readonly OcrLine[]): PageSpots {
  const lines: Placed[] = ocrLines.flatMap((line) => {
    const box = boxOf(line);
    const text = line.text.trim();
    return box && text ? [{ text, box, mid: box.y + box.height / 2 }] : [];
  });
  if (!lines.length) return { blocks: [], tables: [], spots: [] };
  const height = Math.max(1, median(lines.map((line) => line.box.height)));
  const left = Math.min(...lines.map((l) => l.box.x)),
    right = Math.max(...lines.map((l) => l.box.x + l.box.width));
  const span = Math.max(1, right - left);
  const top = Math.min(...lines.map((l) => l.box.y)),
    bottom = Math.max(...lines.map((l) => l.box.y + l.box.height));

  // Tables: runs of rows with three or more pieces across the page, at least two
  // of them numbers (a quantity, a price, an amount). Label-and-value blocks carry
  // one number a row and stay out.
  const rows = rowsOf(lines, height);
  const tabular = (row: Placed[]) =>
    row.length >= 3 &&
    row.filter((line) => numeric.test(line.text)).length >= 2 &&
    row[row.length - 1].box.x + row[row.length - 1].box.width - row[0].box.x >= span * 0.45;
  const tables: TableSpot[] = [];
  const inTable = new Set<Placed>();
  for (let start = 0; start < rows.length;) {
    let end = start;
    while (
      end < rows.length &&
      tabular(rows[end]) &&
      (end === start || rows[end][0].mid - rows[end - 1][0].mid <= height * 3.2)
    )
      end += 1;
    if (end - start >= 3) {
      // The heading row just above, when it lines up as a row of its own.
      const head =
        start > 0 &&
        rows[start - 1].length >= 3 &&
        rows[start][0].mid - rows[start - 1][0].mid <= height * 3.2 &&
        rows[start - 1].every((line) => !numeric.test(line.text))
          ? start - 1
          : start;
      const members = rows.slice(head, end);
      members.flat().forEach((line) => inTable.add(line));
      tables.push({
        box: union(members.flat().map((line) => line.box)),
        rows: members.map((row) => union(row.map((line) => line.box))),
      });
      start = end;
    } else start = Math.max(start + 1, end);
  }

  // Text blocks: the rest, gathered where lines stack closely and overlap.
  const blocks: Box[] = [];
  for (const line of lines.filter((item) => !inTable.has(item)).sort((a, b) => a.box.y - b.box.y)) {
    const block = blocks.find(
      (candidate) =>
        line.box.y - (candidate.y + candidate.height) <= height * 0.9 &&
        line.box.y + line.box.height >= candidate.y - height * 0.2 &&
        line.box.x <= candidate.x + candidate.width + height &&
        line.box.x + line.box.width >= candidate.x - height,
    );
    if (block) Object.assign(block, union([block, line.box]));
    else blocks.push({ ...line.box });
  }

  // What someone scanning the page would look for first.
  const spots: Spot[] = [];
  const labelFor = (line: Placed, fallback: string) => {
    const inline = line.text.match(/^([^:]{2,26}):/);
    if (inline && isLabel(inline[1]) && roleWord.test(inline[1])) return labelText(inline[1]);
    const row = rows.find((candidate) => candidate.includes(line)) ?? [];
    const before = row
      .filter(
        (other) =>
          other.box.x + other.box.width <= line.box.x + 2 &&
          isLabel(other.text) &&
          roleWord.test(other.text),
      )
      .pop();
    if (before) return labelText(before.text);
    const above = lines
      .filter(
        (other) =>
          other !== line &&
          isLabel(other.text) &&
          roleWord.test(other.text) &&
          other.box.y + other.box.height <= line.box.y + 2 &&
          line.box.y - (other.box.y + other.box.height) <= height * 1.6 &&
          Math.abs(other.box.x - line.box.x) <= height * 2,
      )
      .sort((a, b) => b.box.y - a.box.y)[0];
    return above ? labelText(above.text) : fallback;
  };
  // "INVOICE (CONT'D)" is still an invoice.
  const bare = (text: string) => text.replace(/\s*\((cont|continued)[^)]*\)\s*$/i, '').trim();
  const title = lines
    .filter(
      (line) =>
        line.box.y <= top + (bottom - top) * 0.3 &&
        /^[A-Za-z][A-Za-z &.'-]{2,30}$/.test(bare(line.text)) &&
        bare(line.text).split(/\s+/).length <= 4 &&
        titleWords.test(bare(line.text)),
    )
    .sort((a, b) => b.box.height - a.box.height)[0];
  if (title)
    spots.push({ kind: 'title', label: 'Looks like', value: bare(title.text), box: title.box });

  const seen = new Set<string>();
  for (const line of lines) {
    if (inTable.has(line)) continue;
    const reference = line.text.match(referenceLike)?.[0];
    if (
      reference &&
      !dateLike.test(line.text) &&
      !seen.has(reference) &&
      spots.filter((s) => s.kind === 'reference').length < 2
    ) {
      seen.add(reference);
      spots.push({
        kind: 'reference',
        label: labelFor(line, 'Reference'),
        value: reference,
        box: line.box,
      });
    }
  }
  for (const line of lines) {
    const date = line.text.match(dateLike)?.[0];
    if (date && !seen.has(date) && spots.filter((s) => s.kind === 'date').length < 2) {
      seen.add(date);
      spots.push({ kind: 'date', label: labelFor(line, 'Date'), value: date, box: line.box });
    }
  }
  // The total: the lowest "total" on the page (after any subtotal), and its amount.
  const totalLabel = lines
    .filter((line) => /\btotal\b/i.test(line.text) && !/sub\s*-?total/i.test(line.text))
    .sort((a, b) => b.mid - a.mid)[0];
  if (totalLabel) {
    const row = rows.find((candidate) => candidate.includes(totalLabel)) ?? [totalLabel];
    const amount = [...row]
      .reverse()
      .map((line) => ({ line, value: line.text.match(money)?.pop() }))
      .find((item) => item.value);
    if (amount?.value)
      spots.push({
        kind: 'total',
        label: labelText(totalLabel.text.replace(money, '')) || 'Total',
        value: amount.value.trim(),
        box: union([totalLabel.box, amount.line.box]),
      });
  }
  return { blocks, tables, spots };
}
